/* =====================================================
   TUNE MFD - Setup flow runtime (state graph walker)
   Mountable for Shopify + Flow Lab preview.
   ===================================================== */
(function () {
  'use strict';

  function cloneBag(obj) {
    var out = {};
    Object.keys(obj || {}).forEach(function (key) {
      out[key] = obj[key];
    });
    return out;
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function formatBody(text) {
    var escaped = escapeHtml(text || '');
    escaped = escaped.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    escaped = escaped.replace(/\n/g, '<br>');
    return escaped;
  }

  function typeLabel(type) {
    if (type === 'question') return 'Question';
    if (type === 'info') return 'Guidance';
    if (type === 'terminal') return 'Outcome';
    return type || '';
  }

  function isVideoUrl(url) {
    if (!url) return false;
    if (/^data:video\//i.test(url)) return true;
    if (/cdn\.shopify\.com\/videos\//i.test(url)) return true;
    return /\.(mp4|webm|ogg|mov)(\?|#|$)/i.test(url);
  }

  function isImageUrl(url) {
    if (!url) return false;
    if (/^data:image\//i.test(url)) return true;
    return /\.(png|jpe?g|gif|webp|svg|avif)(\?|#|$)/i.test(url);
  }

  function applyMediaOverrides(graph) {
    var media = window.TUNE_SETUP_MEDIA || {};
    if (!graph || !graph.nodes) return graph;
    if (media.wiringDiagram && graph.nodes.wiring_diagram) {
      graph.nodes.wiring_diagram.image = media.wiringDiagram;
    }
    return graph;
  }

  function mount(root, graph) {
    if (!root) {
      return null;
    }

    if (typeof root._tuneSetupDestroy === 'function') {
      root._tuneSetupDestroy();
    }

    graph = applyMediaOverrides(graph);

    if (!graph || !graph.nodes || !graph.initial) {
      root.innerHTML = '<p class="setup_error">Setup flow data failed to load.</p>';
      return null;
    }

    var stageEl = root.querySelector('[data-setup-stage]');
    var typeEl = root.querySelector('[data-setup-type]');
    var titleEl = root.querySelector('[data-setup-title]');
    var bodyEl = root.querySelector('[data-setup-body]');
    var mediaEl = root.querySelector('[data-setup-media]');
    var choicesEl = root.querySelector('[data-setup-choices]');
    var backBtn = root.querySelector('[data-setup-back]');
    var progressEl = root.querySelector('[data-setup-progress-label]');
    var restartBtn = root.querySelector('[data-setup-restart]');

    if (!titleEl || !bodyEl || !choicesEl) {
      root.innerHTML = '<p class="setup_error">Setup flow markup is incomplete.</p>';
      return null;
    }

    var currentId = graph.initial;
    var bag = {};
    var history = [];
    var destroyed = false;
    var mediaLoopTimer = null;
    var mediaRaf = null;

    function getNode(id) {
      return graph.nodes[id] || null;
    }

    function clearMediaTimers() {
      if (mediaLoopTimer) {
        clearTimeout(mediaLoopTimer);
        mediaLoopTimer = null;
      }
      if (mediaRaf) {
        cancelAnimationFrame(mediaRaf);
        mediaRaf = null;
      }
    }

    function renderMedia(node) {
      if (!mediaEl) return;
      clearMediaTimers();
      mediaEl.innerHTML = '';
      var video = (node.video || '').trim();
      var image = (node.image || '').trim();
      if (!video && !image) {
        mediaEl.hidden = true;
        return;
      }
      mediaEl.hidden = false;

      if (image && !video) {
        var figure = document.createElement('div');
        figure.className = 'setup_image';
        var img = document.createElement('img');
        img.className = 'setup_image_el';
        img.src = image;
        img.alt = node.imageAlt || node.title || 'Wiring diagram';
        img.loading = 'lazy';
        img.decoding = 'async';
        figure.appendChild(img);
        mediaEl.appendChild(figure);
        return;
      }

      if (isVideoUrl(video)) {
        var wrap = document.createElement('div');
        wrap.className = 'setup_video';

        var vid = document.createElement('video');
        vid.className = 'setup_video_el';
        vid.src = video;
        vid.controls = false;
        vid.autoplay = true;
        vid.muted = true;
        vid.defaultMuted = true;
        vid.setAttribute('muted', '');
        vid.playsInline = true;
        vid.setAttribute('playsinline', '');
        vid.setAttribute('webkit-playsinline', '');
        vid.preload = 'auto';
        vid.setAttribute('disablepictureinpicture', '');
        vid.setAttribute('controlslist', 'nodownload noplaybackrate noremoteplayback');
        vid.disablePictureInPicture = true;

        var track = document.createElement('div');
        track.className = 'setup_video_progress';
        track.setAttribute('aria-hidden', 'true');
        var bar = document.createElement('div');
        bar.className = 'setup_video_progress_bar';
        track.appendChild(bar);

        function setProgress(ratio) {
          var pct = Math.max(0, Math.min(1, ratio || 0)) * 100;
          bar.style.width = pct + '%';
        }

        function tickProgress() {
          if (destroyed || !vid.duration || !isFinite(vid.duration)) {
            mediaRaf = null;
            return;
          }
          setProgress(vid.currentTime / vid.duration);
          if (!vid.paused && !vid.ended) {
            mediaRaf = requestAnimationFrame(tickProgress);
          } else {
            mediaRaf = null;
          }
        }

        function startProgressLoop() {
          if (mediaRaf) cancelAnimationFrame(mediaRaf);
          mediaRaf = requestAnimationFrame(tickProgress);
        }

        function restartAfterPause() {
          if (destroyed) return;
          setProgress(0);
          vid.currentTime = 0;
          var playPromise = vid.play();
          if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(function () {});
          }
          startProgressLoop();
        }

        vid.addEventListener('loadedmetadata', function () {
          setProgress(0);
        });

        vid.addEventListener('play', startProgressLoop);

        vid.addEventListener('timeupdate', function () {
          if (vid.duration && isFinite(vid.duration)) {
            setProgress(vid.currentTime / vid.duration);
          }
        });

        vid.addEventListener('ended', function () {
          setProgress(1);
          clearMediaTimers();
          mediaLoopTimer = setTimeout(restartAfterPause, 3000);
        });

        wrap.appendChild(vid);
        wrap.appendChild(track);
        mediaEl.appendChild(wrap);

        var playPromise = vid.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(function () {});
        }
        startProgressLoop();
      } else {
        var frame = document.createElement('iframe');
        frame.src = video;
        frame.title = node.title || 'Setup video';
        frame.setAttribute('allowfullscreen', '');
        frame.setAttribute('loading', 'lazy');
        mediaEl.appendChild(frame);
      }
    }

    function render() {
      if (destroyed) return;

      var node = getNode(currentId);
      if (!node) {
        titleEl.textContent = 'Broken path';
        bodyEl.textContent = 'Node "' + currentId + '" is missing from the graph.';
        choicesEl.innerHTML = '';
        if (mediaEl) {
          mediaEl.innerHTML = '';
          mediaEl.hidden = true;
        }
        return;
      }

      root.setAttribute('data-setup-progress', String(history.length));
      root.setAttribute('data-setup-node', currentId);
      root.setAttribute('data-setup-node-type', node.type || '');

      if (typeEl) {
        typeEl.textContent = typeLabel(node.type);
      }
      titleEl.textContent = node.title || '';
      bodyEl.innerHTML = formatBody(node.body || '');
      renderMedia(node);

      if (progressEl) {
        progressEl.textContent = history.length === 0
          ? 'Start'
          : 'Step ' + (history.length + 1);
      }

      if (backBtn) {
        backBtn.hidden = history.length === 0;
        backBtn.disabled = history.length === 0;
      }

      if (restartBtn) {
        restartBtn.hidden = history.length === 0 && node.type !== 'terminal';
      }

      choicesEl.innerHTML = '';
      var choices = node.choices || [];
      choices.forEach(function (choice) {
        if (choice.separatorBefore || choice.separatorTitle) {
          var sepWrap = document.createElement('div');
          sepWrap.className = 'setup_choice_break';
          if (choice.separatorTitle) {
            var sepTitle = document.createElement('p');
            sepTitle.className = 'setup_choice_section_title';
            sepTitle.textContent = choice.separatorTitle;
            sepWrap.appendChild(sepTitle);
          } else {
            var sep = document.createElement('div');
            sep.className = 'setup_choice_separator';
            sep.setAttribute('role', 'separator');
            sep.setAttribute('aria-hidden', 'true');
            sepWrap.appendChild(sep);
          }
          choicesEl.appendChild(sepWrap);
        }

        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'setup_choice';
        if (choice.image) {
          btn.className += ' setup_choice--has-image';
        }
        if (choice.variant === 'aside') {
          btn.className += ' setup_choice--aside';
        }
        btn.setAttribute('data-choice-id', choice.id);

        if (choice.image) {
          var media = document.createElement('span');
          media.className = 'setup_choice_media';
          var img = document.createElement('img');
          img.src = choice.image;
          img.alt = choice.imageAlt || choice.label || '';
          img.loading = 'lazy';
          img.decoding = 'async';
          media.appendChild(img);
          btn.appendChild(media);
        }

        var text = document.createElement('span');
        text.className = 'setup_choice_label';
        text.textContent = choice.label || choice.id;
        btn.appendChild(text);

        btn.addEventListener('click', function () {
          choose(choice);
        });
        choicesEl.appendChild(btn);
      });

      if (stageEl) {
        stageEl.classList.remove('setup_stage--enter');
        void stageEl.offsetWidth;
        stageEl.classList.add('setup_stage--enter');
      }
    }

    function choose(choice) {
      if (!choice || !choice.next) {
        return;
      }
      if (!getNode(choice.next)) {
        titleEl.textContent = 'Broken edge';
        bodyEl.textContent = 'Choice leads to missing node "' + choice.next + '".';
        choicesEl.innerHTML = '';
        return;
      }

      if (choice.clearHistory) {
        history = [];
        bag = {};
      } else {
        history.push({
          nodeId: currentId,
          choiceId: choice.id,
          bagSnapshot: cloneBag(bag)
        });
      }

      if (choice.set && typeof choice.set === 'object') {
        Object.keys(choice.set).forEach(function (key) {
          bag[key] = choice.set[key];
        });
      }

      currentId = choice.next;
      render();
    }

    function goBack() {
      if (history.length === 0) {
        return;
      }
      var prev = history.pop();
      currentId = prev.nodeId;
      bag = cloneBag(prev.bagSnapshot);
      render();
    }

    function restart() {
      currentId = graph.initial;
      bag = {};
      history = [];
      render();
    }

    function destroy() {
      destroyed = true;
      clearMediaTimers();
      if (backBtn) backBtn.removeEventListener('click', goBack);
      if (restartBtn) restartBtn.removeEventListener('click', restart);
      choicesEl.innerHTML = '';
      if (mediaEl) {
        mediaEl.innerHTML = '';
        mediaEl.hidden = true;
      }
      root._tuneSetupDestroy = null;
      root._tuneSetupApi = null;
    }

    if (backBtn) {
      backBtn.addEventListener('click', goBack);
    }
    if (restartBtn) {
      restartBtn.addEventListener('click', restart);
    }

    var api = {
      destroy: destroy,
      restart: restart,
      render: render,
      getState: function () {
        return {
          currentId: currentId,
          bag: cloneBag(bag),
          depth: history.length
        };
      }
    };

    root._tuneSetupDestroy = destroy;
    root._tuneSetupApi = api;
    render();
    return api;
  }

  window.TUNE_SetupFlow = {
    mount: mount
  };

  var autoloadRoot = document.querySelector('[data-setup-flow][data-setup-autoload]');
  if (!autoloadRoot) {
    autoloadRoot = document.querySelector('[data-setup-flow]:not([data-setup-preview])');
  }
  if (autoloadRoot && window.TUNE_SETUP_FLOW && !autoloadRoot.hasAttribute('data-setup-preview')) {
    mount(autoloadRoot, window.TUNE_SETUP_FLOW);
  }
})();
